import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // Add noindex meta tag
  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);

    // Update page title
    document.title = 'Privacy Policy | Virtual Red Air';

    return () => {
      document.head.removeChild(metaTag);
      document.title = 'Virtual Red Air | Synthetic Training Ecosystems';
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-red-900/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="text-2xl font-bold">
              <span className="text-white">Virtual</span>
              <span className="text-red-600"> Red Air</span>
            </button>
            <button onClick={() => navigate('/')} className="hover:text-red-500 transition-colors">
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-8">
            <span className="text-white">Privacy</span> <span className="text-red-500">Policy</span>
          </h1>

          <div className="space-y-8 text-gray-300 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
              <p>
                Virtual Red Air PC ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website virtualredair.com and use our services.
              </p>
              <p className="mt-4">
                Please read this privacy policy carefully. By using our website, you consent to the data practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Company Information</h2>
              <p><strong>Company Name:</strong> Virtual Red Air PC</p>
              <p><strong>Location:</strong> Patras, Greece</p>
              <p><strong>Email:</strong> <a href="mailto:info@virtualredair.com" className="text-red-400 hover:text-red-300">info@virtualredair.com</a></p>
              <p><strong>VAT Number:</strong> [placeholder]</p>
              <p><strong>GEMI Number:</strong> [placeholder]</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Personal Data We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Contact Form Data</h3>
              <p>When you submit our contact form, we collect:</p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>First name and last name</li>
                <li>Email address</li>
                <li>Phone number (optional)</li>
                <li>Industry information (optional)</li>
                <li>Message content</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Automatically Collected Data</h3>
              <p>When you visit our website, we may automatically collect:</p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>IP address (anonymized)</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Pages visited and time spent</li>
                <li>Referring website</li>
                <li>Geographic location (country/city level)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Use of Personal Data</h2>
              <p>We use your personal data for the following purposes:</p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>To respond to your inquiries and requests</li>
                <li>To provide information about our services</li>
                <li>To communicate with you about our offerings</li>
                <li>To improve our website and services</li>
                <li>To analyze website usage and performance</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Google Analytics</h2>
              <p>
                We use Google Analytics 4 to analyze website traffic and usage patterns. Google Analytics uses cookies to collect information about how visitors use our site. This information is used to compile reports and help us improve our website.
              </p>
              <p className="mt-4">
                The data collected by Google Analytics includes:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>Pages viewed and navigation patterns</li>
                <li>Time spent on the website</li>
                <li>Device and browser information</li>
                <li>Anonymized IP addresses</li>
                <li>Geographic location (country/city)</li>
              </ul>
              <p className="mt-4">
                Google Analytics only activates after you accept analytics cookies through our cookie consent banner. You can learn more about how Google uses data at{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300">
                  https://policies.google.com/privacy
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Cookies</h2>
              <p>
                We use cookies to enhance your browsing experience. Cookies are small text files stored on your device that help us understand how you use our website.
              </p>
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">Types of Cookies We Use:</h3>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
                <li><strong>Analytics Cookies:</strong> Used by Google Analytics to understand website usage (requires consent)</li>
                <li><strong>Preference Cookies:</strong> Remember your cookie consent choices</li>
              </ul>
              <p className="mt-4">
                You can control cookie preferences through our cookie consent banner or your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Data Retention</h2>
              <p>
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>Contact form data: Retained until the inquiry is resolved or for up to 2 years</li>
                <li>Analytics data: Retained according to Google Analytics retention settings (14 months by default)</li>
                <li>Cookie consent records: Retained for 12 months</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Your Rights Under GDPR</h2>
              <p>
                As a data subject under the General Data Protection Regulation (GDPR), you have the following rights:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Right to Restrict Processing:</strong> Request limitation of data processing</li>
                <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
                <li><strong>Right to Object:</strong> Object to processing of your data</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:info@virtualredair.com" className="text-red-400 hover:text-red-300">
                  info@virtualredair.com
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                <li>Encrypted data transmission (SSL/TLS)</li>
                <li>Secure server infrastructure</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
              </ul>
              <p className="mt-4">
                However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Policy Updates</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this Privacy Policy periodically.
              </p>
              <p className="mt-4">
                <strong>Last Updated:</strong> January 30, 2026
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-4 p-4 bg-red-900/20 rounded-lg border border-red-900/30">
                <p><strong>Email:</strong> <a href="mailto:info@virtualredair.com" className="text-red-400 hover:text-red-300">info@virtualredair.com</a></p>
                <p><strong>Company:</strong> Virtual Red Air PC</p>
                <p><strong>Location:</strong> Patras, Greece</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
